import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import {
  CreateIncentiveItemApiSchema,
  UpdateIncentiveItemApiSchema,
} from '../schema/incentive.schema'

const c = initContract()

const prefix = '/v2'

// File Schema
const FileSchema = z
  .object({
    id: z.string(),
    s3Key: z.string().nullable().optional(),
    url: z.string().nullable().optional(),
    aggregateId: z.string().nullable().optional(),
    fileType: z.string().nullable().optional(),
    fileVariant: z.string().nullable().optional(),
    createdAt: z.union([z.string(), z.date()]).optional(),
    updatedAt: z.union([z.string(), z.date()]).optional(),
  })
  .passthrough() // Allow additional Prisma fields

// Incentive Category Schema
const IncentiveCategorySchema = z
  .object({
    id: z.string(),
    parent_id: z.string().nullable().optional(),
    name: z.string(),
  })
  .passthrough() // Allow additional Prisma fields

// Incentive Item Voucher Code Schema
const IncentiveItemVoucherCodeSchema = z
  .object({
    id: z.string(),
    incentiveItemId: z.string(),
    code: z.string(),
    details: z.string().nullable().optional(),
    isUsed: z.boolean(),
    endUserId: z.string().nullable().optional(),
    selectedAt: z.string().nullable().optional(),
    fileId: z.string().nullable().optional(),
  })
  .passthrough() // Allow additional Prisma fields

// Incentive Incentive Category Schema (join table)
const IncentiveIncentiveCategorySchema = z
  .object({
    id: z.string(),
    incentiveId: z.string().optional(), // Made optional since it might not be in the response
    incentiveCategoryId: z.string(),
    incentiveCategory: IncentiveCategorySchema.optional(),
  })
  .passthrough() // Allow additional Prisma fields

// Main Incentive Item Schema
const IncentiveItemSchema = z
  .object({
    id: z.string(),
    campaignId: z.string().nullable().optional(),
    customerId: z.string().nullable().optional(),
    name: z.string(),
    price: z.number(),
    description: z.string(),
    isAvailable: z.boolean().nullable().optional(),
    picture: z.string().nullable().optional(),
    type: z.string(),
    skuCode: z.string().nullable().optional(),
    integrationId: z.string().nullable().optional(),
    digitalCode: z.string().nullable().optional(),
    categoryId: z.string().nullable().optional(),
    customer: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .nullable()
      .optional(),
    categories: z.array(IncentiveIncentiveCategorySchema).nullable().optional(),
    voucherCodes: z.array(IncentiveItemVoucherCodeSchema).nullable().optional(),
    voucherCodesFilePath: z.string().nullable().optional(),
    isActive: z.boolean(),
    isAttachedToLotteryCampaign: z.boolean().optional(),
    incentiveAttachedToCampaign: z.number().optional(),
    files: z.array(FileSchema).nullable().optional(),
    isClaimed: z.boolean().optional(),
  })
  .passthrough() // Allow additional fields from Prisma

// Incentive Store Item Schema (used in store list)
const IncentiveStoreItemSchema = z.object({
  incentive: IncentiveItemSchema,
  featured: z.number(),
  totalClaimedIncentives: z.number(),
  files: z.array(FileSchema),
})

// Pagination Schema
const PaginationSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
})

// Pagination Input Schema
export const PaginationInputSchema = z.object({
  page: z.number().optional(),
  pageSize: z.number().optional(),
})

// Filters Input Schema
export const FiltersInputSchema = z.object({
  search: z.string().optional(),
  customerId: z.string().optional(),
  categoryId: z.string().optional(),
})

// Incentive Store Response Schema
const IncentiveStoreResponseSchema = z.object({
  pagination: PaginationSchema,
  incentives: z.array(IncentiveStoreItemSchema),
})

// Use schemas from incentive.schema.ts for consistency with frontend
const CreateIncentiveItemInputSchema = CreateIncentiveItemApiSchema
const UpdateIncentiveItemInputSchema = UpdateIncentiveItemApiSchema

// Export types for backward compatibility
export type CreateIncentiveItemInput = z.infer<
  typeof CreateIncentiveItemInputSchema
>
export type UpdateIncentiveItemInput = z.infer<
  typeof UpdateIncentiveItemInputSchema
>

export const incentive = c.router({
  getCategories: {
    method: 'GET',
    path: `${prefix}/incentives/categories`,
    responses: {
      200: z.array(IncentiveCategorySchema),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get all incentive categories',
  },
  getAll: {
    method: 'GET',
    path: `${prefix}/incentives`,
    responses: {
      200: z.array(IncentiveItemSchema),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get all incentives',
  },
  getByCategory: {
    method: 'GET',
    path: `${prefix}/incentives/by-category`,
    query: z.object({
      incentiveCategoryId: z.string().optional(),
      customerId: z.string().optional(),
      campaignId: z.string().optional(),
    }),
    responses: {
      200: z.array(IncentiveItemSchema),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get incentives by category',
  },
  getStore: {
    method: 'GET',
    path: `${prefix}/incentives/store`,
    query: z.object({
      page: z
        .union([z.string(), z.number()])
        .optional()
        .transform((val) => (val ? String(val) : undefined)),
      pageSize: z
        .union([z.string(), z.number()])
        .optional()
        .transform((val) => (val ? String(val) : undefined)),
      search: z.string().optional(),
      customerId: z.string().optional(),
      categoryId: z.string().optional(),
    }),
    responses: {
      200: IncentiveStoreResponseSchema,
      500: z.object({ message: z.string() }),
    },
    summary: 'Get incentives store with pagination and filters',
  },
  getById: {
    method: 'GET',
    path: `${prefix}/incentives/:id`,
    pathParams: z.object({
      id: z.string(),
    }),
    responses: {
      200: IncentiveItemSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get incentive by ID',
  },
  create: {
    method: 'POST',
    path: `${prefix}/incentives`,
    query: z.object({
      campaignId: z.string().optional(), // For lottery validation
    }),
    body: CreateIncentiveItemInputSchema,
    responses: {
      200: z.object({ id: z.string() }),
      400: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Create incentive item',
  },
  update: {
    method: 'PUT',
    path: `${prefix}/incentives/:id`,
    pathParams: z.object({
      id: z.string(),
    }),
    query: z.object({
      campaignId: z.string().optional(), // For lottery validation
    }),
    body: UpdateIncentiveItemInputSchema,
    responses: {
      200: z.object({ id: z.string() }),
      400: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Update incentive item',
  },
  delete: {
    method: 'DELETE',
    path: `${prefix}/incentives/:id`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: c.noBody(),
    responses: {
      200: z.object({ id: z.string() }),
      401: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Delete incentive item',
  },
})
